package com.bikompanion.domain.usecase

import dev.zacsweers.metro.Inject
import com.bikompanion.domain.BikeNetwork
import com.bikompanion.domain.DeviceTimeZone
import com.bikompanion.domain.StartPosition
import com.bikompanion.domain.distanceKmBetween
import com.bikompanion.domain.timeZoneCenter

/**
 * How far the nearest network may be from the time zone's city and still be where the map opens.
 *
 * A zone covers a whole country or more, so the city it is named after is only a rough guess at
 * where the rider is. Past this, opening on the zone's city is closer to the truth than opening on a
 * network in another country.
 */
private const val MaxNetworkDistanceKm = 300.0

/**
 * Guesses where to open the map without asking for location.
 *
 * Starts from the city the device's time zone is named after, then moves to the bike network
 * nearest to it, if one is close enough. Null when the time zone gives no city.
 */
@Inject
class GuessStartPositionUseCase(
    private val deviceTimeZone: DeviceTimeZone,
) {
    operator fun invoke(networks: List<BikeNetwork>): StartPosition? {
        val zoneCity = deviceTimeZone.id()?.let(::timeZoneCenter) ?: return null
        val nearest = networks
            .map { it to distanceKmBetween(zoneCity.latitude, zoneCity.longitude, it.latitude, it.longitude) }
            .minByOrNull { (_, distanceKm) -> distanceKm }
            ?.takeIf { (_, distanceKm) -> distanceKm <= MaxNetworkDistanceKm }
            ?.first
            ?: return zoneCity
        return StartPosition(latitude = nearest.latitude, longitude = nearest.longitude)
    }
}
